import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  ChevronUp, ChevronDown, Dumbbell, HeartPulse, Languages, MessageSquare,
  MessageCircle, Send, SpellCheck, Sparkles, User, Zap, Loader2,
  EyeOff, Flag, CheckCircle2,
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { SendEmailButton } from '@/components/email/SendEmailButton';
import { cn } from '@/lib/utils';
import { useAIAssistant } from '@/hooks/useAIAssistant';

export const formatNoteRelativeTime = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const now = new Date();
  const diffSeconds = Math.round((now - date) / 1000);
  const diffMinutes = Math.round(diffSeconds / 60);
  const diffHours = Math.round(diffMinutes / 60);
  if (diffSeconds < 60) return 'just now';
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

export const NOTE_FLAG_REASONS = [
  { value: 'pain_injury', label: 'Pain / injury concern' },
  { value: 'needs_head_coach', label: 'Needs head coach answer' },
  { value: 'program_change', label: 'Program change needed' },
  { value: 'technical', label: 'Technical / app problem' },
  { value: 'other', label: 'Other' },
];

const flagReasonLabel = (value) =>
  NOTE_FLAG_REASONS.find((r) => r.value === value)?.label || value;

const FlagNoteDialog = ({ open, onOpenChange, onSubmit }) => {
  const [reason, setReason] = useState('');
  const [comment, setComment] = useState('');
  const [saving, setSaving] = useState(false);
  const commentRequired = reason === 'other';
  const canSubmit = reason && (!commentRequired || comment.trim()) && !saving;

  const handleSubmit = async () => {
    setSaving(true);
    try {
      await onSubmit(reason, comment.trim());
      setReason('');
      setComment('');
      onOpenChange(false);
    } catch {
      // The caller reports the error; keep the dialog open so the user can retry.
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Flag note for the head coach</DialogTitle>
          <DialogDescription>The head coach sees flagged notes in Live Studio.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Select value={reason} onValueChange={setReason}>
            <SelectTrigger>
              <SelectValue placeholder="Choose a reason..." />
            </SelectTrigger>
            <SelectContent>
              {NOTE_FLAG_REASONS.map((r) => (
                <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Textarea
            placeholder={commentRequired ? 'Comment (required)' : 'Comment (optional)'}
            value={comment}
            maxLength={1000}
            onChange={(e) => setComment(e.target.value)}
            className="min-h-[80px] text-sm resize-none"
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Flag className="h-4 w-4 mr-1" />}
            Flag note
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const NoteReplyInput = ({ completionId, onReply, isSubmitting, text, setText }) => {
  const ai = useAIAssistant();

  const handleSubmit = async () => {
    if (!text.trim()) return;
    try {
      await onReply(completionId, text.trim());
      setText('');
      toast.success('Reply sent', { description: 'The user will be notified.' });
    } catch (err) {
      toast.error('Failed to send reply', { description: err.message });
    }
  };

  const handleFixGrammar = async () => {
    if (!text.trim()) return;
    try {
      const fixed = await ai.fixGrammar(text, 'en');
      if (fixed === text) {
        toast.info('No grammar issues found');
      } else {
        setText(fixed);
        toast.success('Grammar corrected');
      }
    } catch {
      toast.error('Grammar check failed');
    }
  };

  const handleTranslateToGerman = async () => {
    if (!text.trim()) return;
    try {
      const translated = await ai.translate(text, 'de');
      setText(translated);
      toast.success('Translated to German');
    } catch {
      toast.error('Translation failed');
    }
  };

  return (
    <div className="mt-2 space-y-2">
      <Textarea
        placeholder="Reply to this note..."
        value={text}
        onChange={(e) => setText(e.target.value)}
        className="min-h-[60px] text-xs resize-none"
        onKeyDown={(e) => {
          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleSubmit();
        }}
      />
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="sm"
            className="text-xs h-7 px-2 text-muted-foreground"
            onClick={handleFixGrammar}
            disabled={!text.trim() || ai.loading}
          >
            {ai.loading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <SpellCheck className="h-3 w-3 mr-1" />}
            Fix Grammar
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="text-xs h-7 px-2 text-muted-foreground"
            onClick={handleTranslateToGerman}
            disabled={!text.trim() || ai.loading}
          >
            {ai.loading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Languages className="h-3 w-3 mr-1" />}
            Translate to DE
          </Button>
        </div>
        <Button
          size="sm"
          onClick={handleSubmit}
          disabled={!text.trim() || isSubmitting}
          className="shrink-0"
        >
          <Send className="h-3.5 w-3.5 mr-1" />
          Send
        </Button>
      </div>
    </div>
  );
};

const NoteCard = ({ note, onReply, compact, onTriage, onFlag, onResolveFlag, currentUserId, isAdmin }) => {
  const [expanded, setExpanded] = useState(false);
  const [flagDialogOpen, setFlagDialogOpen] = useState(false);
  const [isResolvingFlag, setIsResolvingFlag] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [translatedText, setTranslatedText] = useState(null);
  const [targetLang, setTargetLang] = useState('de');
  const [isUpdatingTriage, setIsUpdatingTriage] = useState(false);
  const ai = useAIAssistant();
  const navigate = useNavigate();
  const replies = Array.isArray(note?.replies) ? note.replies : [];
  if (!note || typeof note !== 'object' || !note.completion_id) return null;
  const triageStatus = note.triage_status || 'needs_reply';
  const canTriage = onTriage && (replies.length === 0 || triageStatus === 'no_reply_needed');
  const flag = note.flag || null;
  const canResolveFlag = Boolean(flag && onResolveFlag && (isAdmin || flag.flagged_by === currentUserId));

  const handleFlagSubmit = async (reason, comment) => {
    try {
      await onFlag(note.completion_id, reason, comment);
      toast.success('Note flagged for the head coach');
    } catch (err) {
      toast.error('Could not flag note', { description: err.message });
      throw err;
    }
  };

  const handleResolveFlag = async () => {
    setIsResolvingFlag(true);
    try {
      await onResolveFlag(flag.flag_id);
      toast.success(isAdmin ? 'Flag resolved' : 'Flag withdrawn');
    } catch (err) {
      toast.error('Could not update flag', { description: err.message });
    } finally {
      setIsResolvingFlag(false);
    }
  };

  const handleReply = async (completionId, text) => {
    setIsSubmitting(true);
    try {
      await onReply(completionId, text);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTranslate = async () => {
    if (!note.notes) return;
    try {
      const result = await ai.translate(note.notes, targetLang);
      setTranslatedText(result);
    } catch {
      toast.error('Translation failed');
    }
  };

  const handleAIReply = async () => {
    try {
      const draft = await ai.generateReply({
        athleteNote: note.notes,
        exerciseName: note.exercise_name,
        painLevel: note.pain_level_reported,
        rpe: note.rpe_reported,
        language: targetLang,
      });
      setReplyText(draft);
      if (!expanded) setExpanded(true);
      toast.success('AI draft generated', { description: 'Review and edit before sending.' });
    } catch {
      toast.error('Failed to generate reply');
    }
  };

  const handleTriageToggle = async () => {
    if (!onTriage) return;
    const nextStatus = triageStatus === 'no_reply_needed' ? 'needs_reply' : 'no_reply_needed';
    setIsUpdatingTriage(true);
    try {
      await onTriage(note.completion_id, nextStatus);
    } finally {
      setIsUpdatingTriage(false);
    }
  };

  return (
    <Card className="overflow-hidden">
      <CardContent className="p-4">
        {!compact && (
          <div className="flex items-start justify-between mb-2">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                <User className="h-4 w-4 text-primary" />
              </div>
              <div>
                <button
                  className="text-sm font-medium hover:text-primary hover:underline transition-colors text-left"
                  onClick={() => note.user_id && navigate(`/user-activity/${note.user_id}`)}
                >
                  {note.full_name}
                </button>
                <div className="flex items-center gap-1">
                  <p className="text-xs text-muted-foreground">{note.email}</p>
                  <SendEmailButton userId={note.user_id} fullName={note.full_name} email={note.email} />
                </div>
              </div>
            </div>
            <span className="text-xs text-muted-foreground">{formatNoteRelativeTime(note.completed_at)}</span>
          </div>
        )}
        <div className={compact ? '' : 'ml-10'}>
          <div className="flex items-center gap-2 mb-1">
            <Badge variant="outline" className="text-xs">
              <Dumbbell className="h-3 w-3 mr-1" />
              {note.exercise_name}
            </Badge>
            {note.session_name && (
              <span className="text-xs text-muted-foreground truncate">{note.session_name}</span>
            )}
            {compact && (
              <span className="text-xs text-muted-foreground ml-auto">{formatNoteRelativeTime(note.completed_at)}</span>
            )}
          </div>

          <p className="text-sm my-2">{note.notes}</p>

          {flag && (
            <div className="my-2 flex items-start gap-2 p-2 rounded-md border border-red-500/40 bg-red-500/10">
              <Flag className="h-3.5 w-3.5 text-red-500 mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs font-semibold text-red-500">{flagReasonLabel(flag.reason)}</span>
                  <span className="text-xs text-muted-foreground">
                    Flagged by {flag.flagged_by_name || 'staff'} · {formatNoteRelativeTime(flag.created_at)}
                  </span>
                </div>
                {flag.comment && <p className="text-xs mt-0.5">{flag.comment}</p>}
              </div>
              {canResolveFlag && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-xs h-7 px-2 shrink-0"
                  onClick={handleResolveFlag}
                  disabled={isResolvingFlag}
                >
                  {isResolvingFlag ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <CheckCircle2 className="h-3 w-3 mr-1" />}
                  {isAdmin ? 'Resolve' : 'Withdraw'}
                </Button>
              )}
            </div>
          )}

          {translatedText && (
            <div className="my-2 p-2 bg-muted/50 rounded-md border border-dashed">
              <p className="text-xs text-muted-foreground mb-0.5 font-medium">
                Translation ({targetLang === 'de' ? 'DE' : 'EN'})
              </p>
              <p className="text-sm italic">{translatedText}</p>
            </div>
          )}

          <div className="flex items-center gap-3 text-xs text-muted-foreground">
            {note.rpe_reported !== null && (
              <span className="flex items-center gap-1">
                <Zap className="h-3 w-3 text-orange-500" />
                RPE: {note.rpe_reported}/10
              </span>
            )}
            {note.pain_level_reported !== null && note.pain_level_reported > 0 && (
              <span className={cn(
                'flex items-center gap-1',
                note.pain_level_reported > 5 ? 'text-red-500' : 'text-amber-500'
              )}>
                <HeartPulse className="h-3 w-3" />
                Pain: {note.pain_level_reported}/10
              </span>
            )}
          </div>

          <div className="flex items-center gap-1 mt-2 flex-wrap">
            <Button
              variant="ghost"
              size="sm"
              className="text-xs h-7 px-2 text-muted-foreground"
              onClick={handleTranslate}
              disabled={ai.loading || !note.notes}
            >
              {ai.loading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Languages className="h-3 w-3 mr-1" />}
              Translate
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="text-xs h-7 px-2 text-muted-foreground"
              onClick={handleAIReply}
              disabled={ai.loading || !note.notes}
            >
              {ai.loading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Sparkles className="h-3 w-3 mr-1" />}
              AI Reply
            </Button>
            <div className="inline-flex items-center rounded-md border text-xs h-7 ml-1">
              <button
                className={cn(
                  'px-2 h-full rounded-l-md transition-colors',
                  targetLang === 'en' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'
                )}
                onClick={() => setTargetLang('en')}
              >
                EN
              </button>
              <button
                className={cn(
                  'px-2 h-full rounded-r-md transition-colors',
                  targetLang === 'de' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'
                )}
                onClick={() => setTargetLang('de')}
              >
                DE
              </button>
            </div>
            {onFlag && !flag && (
              <Button
                variant="ghost"
                size="sm"
                className="text-xs h-7 px-2 text-muted-foreground"
                onClick={() => setFlagDialogOpen(true)}
              >
                <Flag className="h-3 w-3 mr-1" />
                Flag
              </Button>
            )}
            {canTriage && (
              <Button
                variant="ghost"
                size="sm"
                className="text-xs h-7 px-2 text-muted-foreground ml-auto"
                onClick={handleTriageToggle}
                disabled={isUpdatingTriage}
              >
                {isUpdatingTriage ? (
                  <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                ) : (
                  <EyeOff className="h-3 w-3 mr-1" />
                )}
                {triageStatus === 'no_reply_needed' ? 'Needs reply' : 'No reply needed'}
              </Button>
            )}
          </div>

          {replies.length > 0 && (
            <div className="mt-3 space-y-2">
              {replies.map((reply) => (
                <div key={reply.id} className="flex gap-2 p-2 bg-primary/5 rounded-md border-l-2 border-primary">
                  <MessageCircle className="h-3.5 w-3.5 text-primary mt-0.5 shrink-0" />
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-semibold text-primary">{reply.admin_name || 'Coach'}</span>
                      <span className="text-xs text-muted-foreground">{formatNoteRelativeTime(reply.created_at)}</span>
                    </div>
                    <p className="text-xs mt-0.5">{reply.reply_text}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="mt-2">
            <Button
              variant="ghost"
              size="sm"
              className="text-xs h-7 px-2"
              onClick={() => setExpanded(!expanded)}
            >
              {expanded ? (
                <><ChevronUp className="h-3 w-3 mr-1" /> Hide Reply</>
              ) : (
                <><MessageSquare className="h-3 w-3 mr-1" /> Reply</>
              )}
            </Button>
          </div>

          {expanded && (
            <NoteReplyInput
              completionId={note.completion_id}
              onReply={handleReply}
              isSubmitting={isSubmitting}
              text={replyText}
              setText={setReplyText}
            />
          )}
        </div>
        {onFlag && (
          <FlagNoteDialog open={flagDialogOpen} onOpenChange={setFlagDialogOpen} onSubmit={handleFlagSubmit} />
        )}
      </CardContent>
    </Card>
  );
};

export default NoteCard;
